import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import { FiMinus, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const { addToCart } = useCart();

  useEffect(() => {
    loadProduct();
    window.scrollTo(0, 0);
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await productsAPI.getById(id);
      setProduct(response.data.product);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product.stock < quantity) {
      toast.error('Insufficient stock');
      return;
    }
    addToCart(product, quantity);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // Construct proper image URL
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    } else if (imageUrl.startsWith('/uploads/')) {
      return `http://localhost:5000${imageUrl}`;
    } else {
      return `http://localhost:5000/uploads/${imageUrl}`;
    }
  };

  if (loading) {
    return <div className="loading-detail"><div className="spinner"></div></div>;
  }

  if (!product) return null;

  const colors = ['#e5e7eb', '#374151', '#92400e'];

  return (
    <div className="product-detail-new container">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/products">Products</Link>
      </div>

      <div className="product-detail-grid">
        {/* Left: Images */}
        <div className="product-images-section-new">
          <div className="main-product-image">
            {(() => {
              const validImages = (product.images || []).filter(img => img && img !== null && img !== '');
              const currentImage = validImages[selectedImage];
              return getImageUrl(currentImage) ? (
                <img
                  src={getImageUrl(currentImage)}
                  alt={product.name}
                  crossOrigin="anonymous"
                  onError={(e) => {
                    console.error('ProductDetail main image error:', getImageUrl(currentImage));
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('✅ ProductDetail main loaded:', getImageUrl(currentImage))}
                />
              ) : (
              <div style={{
                width: '100%',
                height: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f3f4f6',
                color: '#9ca3af',
                fontSize: '16px'
              }}>
                <svg width="100" height="100" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="#9ca3af"/>
                </svg>
                <span style={{ marginTop: '12px' }}>No Image Available</span>
              </div>
              );
            })()}
          </div>
          <div className="thumbnail-images">
            {(() => {
              const validImages = (product.images || []).filter(img => img && img !== null && img !== '');
              return validImages.length > 0 ? (
                validImages.slice(0, 3).map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  {getImageUrl(img) ? (
                    <img
                      src={getImageUrl(img)}
                      alt={`${product.name} ${index + 1}`}
                      crossOrigin="anonymous"
                      onError={(e) => {
                        console.error('ProductDetail thumbnail error:', getImageUrl(img));
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f3f4f6',
                      color: '#9ca3af',
                      fontSize: '12px'
                    }}>
                      <span>No Image</span>
                    </div>
                  )}
                </div>
              ))
              ) : (
                <div className="thumbnail active">
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f3f4f6',
                    color: '#9ca3af',
                    fontSize: '12px'
                  }}>
                    <span>No Image</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Right: Product Info */}
        <div className="product-info-section-new">
          <h1 className="product-title-new">{product.name}</h1>
          <div className="product-price-new">${product.price.toFixed(2)}</div>

          {/* Description */}
          <div className="product-description-new">
            <h3>Description</h3>
            <p>
              {product.description ||
                'Loose-fit sweatshirt hoodie in medium weight cotton-blend fabric with a generous, but not oversized silhouette. Jersey-lined, drawstring hood.'}
            </p>
          </div>

          {/* Color Selector */}
          <div className="color-selector">
            <h3>Color</h3>
            <div className="color-options">
              {colors.map((color, index) => (
                <button
                  key={index}
                  className={`color-btn ${selectedColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setSelectedColor(color)}
                ></button>
              ))}
            </div>
          </div>

          {/* Quantity & Actions */}
          <div className="quantity-actions">
            <div className="quantity-selector-new">
              <button
                className="qty-btn"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                <FiMinus />
              </button>
              <span className="qty-display">{quantity}</span>
              <button
                className="qty-btn"
                onClick={() =>
                  setQuantity(Math.min(product.stock || 999, quantity + 1))
                }
              >
                <FiPlus />
              </button>
            </div>
            <button className="btn-add-cart" onClick={handleAddToCart}>
              Add to Cart
            </button>
            <button className="btn-buy-now" onClick={handleAddToCart}>
              Buy Now
            </button>
          </div>

          {/* Shipping Info */}
          <div className="shipping-section">
            <div className="shipping-header">
              <span className="icon">📦</span>
              <h3>Shipping</h3>
            </div>
            <div className="shipping-details">
              <div className="shipping-item">
                <span className="icon">💰</span>
                <div>
                  <strong>Disc 50%</strong>
                  <p>Package Reg</p>
                </div>
              </div>
              <div className="shipping-item">
                <span className="icon">⏱️</span>
                <div>
                  <strong>Delivery Time</strong>
                  <p>3-4 Working Days</p>
                </div>
              </div>
            </div>
            <div className="shipping-details">
              <div className="shipping-item">
                <span className="icon">📍</span>
                <div>
                  <strong>Arrive</strong>
                  <p>0 - 12 Oct 2024</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rating & Reviews Section */}
      <div className="reviews-section">
        <h2>Rating & Reviews</h2>
        <div className="rating-summary">
          <div className="rating-score">
            <div className="score">4.5</div>
            <div className="stars">★★★★★</div>
            <p className="review-count">(50 New Reviews)</p>
          </div>
          <div className="rating-bars">
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="rating-bar-item">
                <span className="star-label">{star} ★</span>
                <div className="bar-container">
                  <div
                    className="bar-fill"
                    style={{
                      width: `${star === 5 ? 80 : star === 4 ? 60 : star === 3 ? 30 : star === 2 ? 10 : 5}%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sample Review */}
        <div className="reviews-list">
          <div className="review-item">
            <div className="review-header">
              <div className="reviewer-info">
                <strong>Obayedul</strong>
                <div className="review-stars">★★★★★</div>
              </div>
              <span className="review-date">13 Oct 2024</span>
            </div>
            <p className="review-text">
              "Loose-fit sweatshirt hoodie in medium weight cotton-blend fabric
              with a generous, but not oversized silhouette. Jersey-lined,
              drawstring hood, dropped shoulders, long sleeves."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
